// src/store.ts (v2.2)
// クライアント状態 (フィルタ) を Zustand で
// v2.2: URL search params との bidirectional sync を追加
import { create } from "zustand";
import type { Iryo, Yotei, Granularity } from "@shared/api";
import { diseaseUrlPeriod, readDpcYear, type DiseaseAutoPeriod } from "./lib/diseasePeriod";
import { IS_THETA } from "./lib/gpMode";

export type PeriodPreset = "月次推移" | "直近12ヶ月平均" | "年度累計";

/** GP分析(粗利分析)タブの「退院日基準」を出すか (2026-08-27 ユーザ指示で false = 封鎖)。
 *
 *  ★封鎖の理由: 退院日基準 (fact_disease_episode) の 医師人件費_日 は **2025-04 以降の患者日**
 *    にしか配賦されていない (build_dashboard_db.R の科別日単価配賦)。そのため 2025-03 以前から
 *    在院していたエピソードは人件費が部分的に欠け、境界月を含む期間で 医師人件費 / GP /
 *    医師人件費比 が構造的に過小になる (実測: 2025-04 退院分 0.64 億 vs 他月 0.99〜1.16 億)。
 *    手当て (境界月の警告 or 人件費のある月だけを分母にする) が済むまで UI から外す。
 *
 *  ★false のとき: サイドバーの「🕒 期間基準」が消え、profitBasis は常に "在院"
 *    (URL の ?pbasis=退院 も無視)。退院日基準でしか出ない 医師別表 / DPC別表 / DPCセレクタも
 *    ProfitPanel 側の isDischarge が false になるので連動して消える。
 *  ★server の basis="退院" は温存してあるので、true に戻すだけで復活する。 */
export const PROFIT_BASIS_DISCHARGE_ENABLED = false;

// [2026-09-28] DPC 階層。DPC 14 桁 = 疾患6 + 病態等分類(7) + 年齢・JCS等(8) + 手術(9-10)
//   + 処置1(11) + 処置2(12) + 副傷病(13) + 重症度等(14)。タブ・URL (dlv)・server と共通。
//   ★store の初期化 (dpcLevel の URL 復元) より前に宣言すること (TDZ)。
export const DPC_LEVELS = [6, 7, 8, 10, 11, 12, 13, 14] as const;

// 経営PL タブ専用の表示形式 (サイドバーの「表示形式」に一元化、 2026-06-15)
export type PlFormat = "月別" | "年度累計" | "直近12ヶ月累計";
// [2026-09-11] 経営PL タブの利益の段階 (サイドバー「利益の段階」)。
//   経常利益 = 医業利益 + 医業外損益 (雑収入+受取利息−雑損失−支払利息)。
//   選択に応じて PL タブの「経費等」の中身と、 利益系の指標そのものが入れ替わる。
//     経常利益 → 経費等 = 経費計+研修費+研究費+本部経費 − 医業外損益 (医業収益計 − 5 費用 = 経常利益)
//     医業利益 → 経費等 = 経費計+研修費+研究費+本部経費            (医業収益計 − 5 費用 = 医業利益)
//   式の正本は lib/plBasis.ts。 恒等式は scripts/check-pl-identity.mjs が実データで守る。
export type PlProfit = "経常利益" | "医業利益";
export type OpeRevenueMode = "手術売上" | "手技売上" | "材料薬剤費";

// [2026-08-22 ユーザ指示] 入院KPI タブは廃止 (使わないので没)。
//   実体の KpiBigPanel は color helper だけ他パネルが使うので残置。
/** 群分類の既定しきい値 (円/日)。
 *  正式版GPは 2026-08-26 ユーザ指示で **100,000 / 80,000 / 60,000** (1万円刻み・旧 85,000/75,000/65,000 から変更)。
 *  ★正式版 (粗利 = 医師人件費を引かない) は 105,000/85,000/65,000 のまま。両者は別スケール。 */
export const GUN_DEFAULT: [number, number, number] = IS_THETA ? [100000, 80000, 60000] : [105000, 85000, 65000];

export type TabKey = "外来収入" | "外来予約" | "収入速報" | "紹介・入院分析" | "退院先分岐" | "職員数・人件費" | "AI分析" | "入院実績" | "入院実績(前年度・予算比較)" | "入院実績(医師別)" | "入院料分析" | "短期滞在3分析" | "要確認診療科" | "入院経路" | "手術" | "行為別" | "明細分析" | "経営PL" | "DPC分析" | "相関分析" | "元データ" | "粗利分析" | "粗利分析(医師別なし)" | "粗利効率分析" | "行為別売上" | "手術売上" | "手術会計明細" | "請求会計速報" | "速報売上" | "看護必要度速報" | "室料差額" | "急性期総合体制加算3" | "データ辞書" | "参照データ一覧" | "給与見込み・GP比較";

// 入院実績系タブ (FilterBar が同じ期間コントロールを出す・InpatientBudgetComparePanel を描画)。
//   "入院実績(前年度・予算比較)" = 正式版 / "入院実績(医師別)" = β (科別マトリックスの下に医師別)
export const INPATIENT_COMPARE_TABS: TabKey[] = ["入院実績(前年度・予算比較)", "入院実績(医師別)"];

// 医師別マトリックスの表示行数 (科フォーカスなしのとき)。印刷 1 枚に収まる範囲で選ぶ。
export const DOCTOR_TOP_N_OPTIONS = [20, 25, 30] as const;
export type DoctorTopN = (typeof DOCTOR_TOP_N_OPTIONS)[number];

// ベータ版 (/beta) でのみ表示する「リリース前タブ」。正式版 TabNav からは除外され、
// /beta ページにのみ載る。正式昇格時はここから外して TabNav.tsx の TABS に戻す。
// 粗利分析は「医師別あり/なし」の2バージョンを並べて比較できるようにしている。
// 2026-07-10: 入院実績(前年度・予算比較) を正式昇格 (BETA_TABS から除外・TabNav 先頭・デフォルトタブ化)
// [2026-07-18 正式昇格] DPC分析・粗利分析(医師別なし・表示名「粗利分析」) をリリース
// (BETA_TABS から除外 → 正式版 TabNav に自動掲載)。粗利分析(医師別あり) はベータ継続。
// 2026-07-18: 行為別売上 を正式昇格 (旧「行為別」タブは非表示・migrateTab で振替)。
// 2026-07-21: 入院KPI/入院詳細実績(旧入院実績)/明細分析/元データ をβへ (ユーザ指示)。
// 2026-08-03: 入院実績(医師別) を β に新設 (正式版の 入院実績 タブはそのまま温存)。
// 2026-08-13: 入院料分析・短期滞在3分析を正式昇格（行為別売上の直後）。
// 2026-08-26: 粗利効率分析 (GP効率分析) を新設 (GP=延数×単価の分解ビュー・ユーザ指示)。
// 2026-09-26: 室料差額 を β に新設 (2026-08 の料金改定と差額室の徴収状況・ユーザ指示)。
//   同日 表示名「個室料」で正式昇格 (短期滞在3の直後。内部キーは URL 互換のため「室料差額」のまま)。
// 2026-09-26: 急性期総合体制加算3 を β に新設 (手術等の集積性の年度累計ペース監視・ユーザ指示) → 同日 正式昇格。
export const BETA_TABS: TabKey[] = [ "外来収入", "給与見込み・GP比較", "入院実績(医師別)", "看護必要度速報", "粗利分析", "粗利効率分析", "入院実績", "明細分析", "元データ", "手術会計明細", "請求会計速報", "速報売上", "データ辞書", "参照データ一覧"];
// 粗利分析系タブ(FilterBar が粗利用コントロールを出す対象)
export const PROFIT_TABS: TabKey[] = ["粗利分析", "粗利分析(医師別なし)", "粗利効率分析"];

// 収入の基準 (入院収入 / 粗利)。
// [共通化 2026-07-22 ユーザ指示] 以前はタブごとに別 state だった 3 つ
//   - 入院実績/入院KPI 「収入の基準」   (旧 kpiRevenueBase)
//   - 行為別売上       「金額基準」     (旧 kbActBasis)
//   - DPC分析          「指標切替」     (旧 diseaseMetricMode: 粗利/収入)
// を 1 つの revenueBasis に統合し、タブを移動しても選択を引き継ぐ。
export type RevenueBasis = "入院収入" | "粗利";
/** @deprecated 旧名 (import 互換のため残置)。新規コードは RevenueBasis を使う。 */
export type KpiRevenueBase = RevenueBasis;
export type KpiVolumeBase  = "入院" | "在院";

// 速報進捗ビュー(請求会計速報)タブの表示モード。
//   入院: 3 表ドリル(表示科別→診療No別→表示科×診療No別)
//   外来: 外来 表示科別 1 表(★UI からは非表示・コード/endpoint は温存し将来復活可能に)
//   入外合計: 入院行+外来行 1 表(= 入院+外来の合計・旧称「病院全体」を 2026-07-04 改名)
export type BillingReconMode = "入院" | "外来" | "入外合計";

// 入院経路タブ: サイドバー「経由」フィルタ (RoutePanel の 経由 軸カテゴリと同じ 3 分類)。
//   合計 = 絞り込みなし (全経由)
export type RouteKeiyu = "合計" | "病院" | "総CL" | "その他";

// 行為別タブの 注目行為 (集計区分) 一覧。サイドバー「注目指標」+ マトリックス強調で共有。
export const KB_ACTS = ["合計", "入院料小計", "手術小計", "処置小計", "検査小計", "注射小計", "投薬小計", "画像診断小計", "医学管理", "食事小計", "自費小計", "その他小計"];

export interface FilterState {
  granularity: Granularity;   // 粒度 (大/中/小)
  iryo:        Iryo;
  yotei:       Yotei;
  dischargeDept: string;
  setDischargeDept: (v: string) => void;
  metric:      string;         // 主要指標 (KPI cards 以外で使う)
  asOfMonth:   string;         // YYYY-MM-DD: 対象月 (KPI / alerts の基準)
  periodFrom:  string;         // YYYY-MM-DD: 期間累計の起点
  periodTo:    string;         // YYYY-MM-DD: 期間累計の終点 (= asOfMonth と同期可)
  periodPreset:PeriodPreset;
  plFormat:    PlFormat;        // 経営PL タブの表示形式 (月別 / 年度累計 / 直近12ヶ月累計)
  plProfit:    PlProfit;        // 経営PL タブの利益の段階 (経常利益 / 医業利益)
  // [共通化 2026-07-22] 収入の基準 (入院収入 / 粗利)。入院実績・入院KPI・行為別売上・DPC分析で共有。
  revenueBasis:   RevenueBasis;
  kpiVolumeBase:  KpiVolumeBase;  // 延数基準 (入院 / 在院)。入院実績・GP分析で使う
  kbAct:          string;         // 行為別タブ: 注目行為 (科別マトリックスの対象 + 行為別マトリックス強調)
  // [2026-07-18] 行為別売上タブ: 表示指標 (グラフ+科×月表の値を切替)
  // [2026-08-17] 入院基本料 + 特定入院料 を「入院料」に統合
  kbActMetric:    "入院収入" | "入院料" | "手術" | "その他";
  // [2026-07-18] 手術売上タブ: 集計期間 (null = 全期間)
  opeFrom:        string | null;
  opeTo:          string | null;
  // [2026-07-23] 手術売上タブ: 手術総額 / 手技 / 材料薬剤の表示を切替
  opeRevenueMode: OpeRevenueMode;
  // [2026-07-23] 手術売上タブ: Kコード / 術式名の部分一致検索
  opeProcedureQuery: string;
  // 明細分析タブ: 区分/基準/診療No分類/診療科 (サイドバーで制御)
  meisaiIo:       "入院" | "外来" | "全";
  meisaiBasis:    "ten" | "yen";
  meisaiSinryoNo: string;         // "" = すべて
  meisaiDept:     string;         // "" = 病院全体
  // ドリルダウン: 特定診療科にフォーカス (null=病院全体)
  focusedDept: string | null;
  // 手術タブ専用 focus (手術台帳の診療科は入院系と別物のため state を分離)
  surgeryFocusedDept: string | null;
  // 手術タブ 集計粒度: 大分類(診療科) ⇄ 小分類(外科→消化器/乳腺内分泌/呼吸器)。
  // サイドバーの診療科リストもこの粒度に追従する (FilterBar)。
  surgeryDim: "大分類" | "小分類";
  // 疾患深掘りタブ専用 filter (DPC 6 桁疾患コード + 入院医師コード)
  dpc6: string | null;
  admdrcd: string | null;
  // 疾患深掘りタブ: 症例数 N 件以上の疾患のみ表示 (default 10)
  minCases: number;
  // 疾患深掘りタブ: DPC 年度 (令和6=2024, 令和8=2026)。 null = 全て
  dpcfy: number | null;
  diseaseAutoPeriod: DiseaseAutoPeriod | null;
  // 疾患深掘りタブ: 期間 override (null = 直近 12 ヶ月 = asOfMonth から自動計算)
  //   - dateFromOverride: "YYYY-MM-01"
  //   - dateToOverride:   "YYYY-MM-DD" (月末想定だが UI からは月選択 → 月末に正規化)
  dateFromOverride: string | null;
  dateToOverride: string | null;
  // 疾患深掘りタブ: 予定緊急 / 医療区分 filter (合計 = filter 解除 = null として server に送る)
  //   既存タブの iryo/yotei (合計/急性期/回復期, 合計/予定/緊急) は fact_long_all の dim 値
  //   (= 集計時の出力グループ名) で意味が異なるため、 疾患タブ専用に別名で管理する。
  //   2026-05-26: 「DPC分析」 タブでは医療区分は 合計/急性期 のみ (= 回復期 は dim 値として
  //   存在するが、 DPC 病院機能上 回復期 を切り分ける意味が薄いため UI から除外)
  diseaseYotei: "合計" | "予定" | "緊急";
  diseaseIryo:  "合計" | "急性期";
  // [2026-09-28 ユーザ指示] DPC分析の件数・金額を「期間累計」か「月平均」かでタブ内統一 (主表/サマリ/ICD別/実績K)。
  //   既定 = 期間累計。中央値・率・単価はどちらでも同じ値。
  dpcAgg: "累計" | "月平均";
  dpcLevel: number;            // [桁切替 2026-06-26] 最初の表: 6=疾患別/10=手術別/11=処置1別/12=処置2別
                               // [2026-09-28] +7=病態等分類/8=年齢・JCS等/13=副傷病/14=全14桁 (DPC_LEVELS)
  // [共通化 2026-07-22] 旧 diseaseMetricMode (粗利/収入) は revenueBasis に統合済。
  // 疾患深掘りタブ: DPC 階層 drill-down (表 2-5) の選択 path。
  //   表 1 (DPC6) クリック → selectedDpc10 を null にして表 2 をリセット (= dpc6 だけ確定)
  //   表 2 (DPC10) クリック → selectedDpc10 確定、 selectedDpc11 をリセット
  //   ...
  // null は「未選択」 = 下位表非表示。
  // [2026-09-28] 7桁目(病態等分類)/8桁目(年齢・JCS等) の選択も同じ前方一致チェーンに入れる。
  selectedDpc7:  string | null;
  selectedDpc8:  string | null;
  selectedDpc10: string | null;
  selectedDpc11: string | null;
  selectedDpc12: string | null;
  selectedDpc13: string | null;
  selectedDpc14: string | null;
  // 相関分析タブ: 期間 (開始/終了月) + 選択中の指標。 サンプル = 期間内の各月。
  //   corrTo="" は「最新データ月まで (auto)」を意味し、 panel/FilterBar が meta.dateMax で解決する。
  corrFrom: string;     // "YYYY-MM-01" (既定 2024-04-01)
  corrTo:   string;     // "YYYY-MM-01" or "" (= 最新月 auto)
  corrSel:  string;     // 選択中の指標 key を csv で順序保持 (先頭=基準)。 "" = 既定セット
  // 粗利分析タブ: 期間基準・群分類閾値(1日粗利単価 円/日)・医師focus
  //   DPC別の絞り込みは DPC分析タブと共通の dpc6/selectedDpc10-14 を使う (2026-07-17 統一)。
  // ★2026-08-27 ユーザ指示: 退院日基準は **PROFIT_BASIS_DISCHARGE_ENABLED=false で封鎖中**
  //   (医師人件費の配賦が 2025-04 以降の患者日にしか無く、それ以前から在院していた
  //    エピソードの人件費が欠けるため、境界月を含む期間で 医師人件費/GP が構造的に過小。
  //    実測: 2025-04 退院分の医師人件費 0.64 億 vs 他月 0.99〜1.16 億)。
  //   値は常に "在院"。フラグを true に戻せば UI ごと復活する (server の basis=退院 は温存)。
  profitBasis: "在院" | "退院";                 // 在院日(fact_long_all・科別のみ・既定) / 退院日(episode・科別/医師別/DPC別)
  // [2026-07-17] 粗利分析専用の期間 (他タブの periodFrom/To から独立)。
  //   null = 最新確定月のみ (default・meta.dateMax で解決)。"YYYY-MM-01" で固定。
  profitFrom: string | null;
  profitTo:   string | null;
  // [2026-07-18] 粗利分析専用の医療区分 (共通 iryo から独立・default=急性期)。在院日基準のみ有効。
  profitIryo: "合計" | "急性期" | "回復期";
  gun1: number; gun2: number; gun3: number;   // 1群≥gun1 / 2群≥gun2 / 3群≥gun3 / 4群<gun3
  focusedDoctor: string | null;                // 医師別→DPC別 絞り込み(admdrcd)
  // 速報進捗ビュー(請求会計速報)タブ: モード切替 + 入院ドリルの選択(表示科 / 診療No)
  billingReconMode:   BillingReconMode;        // 入院(既定) / 外来(UI 非表示) / 入外合計
  billingReconDept:   string | null;           // 選択中の表示科cd(入院ドリル②③絞込・null=全科)
  billingReconBunrui: string | null;           // 選択中の診療No(入院ドリル③絞込・null=全診療No)
  billingCompareMonth: string | null;          // 4層比較の選択月('YYYY-MM-01'・null=最新月)
  // [2026-08-03] 入院実績(医師別): 医師別マトリックスの表示行数 (科フォーカスなしのとき)。
  //   科フォーカス中はその科の医師を全員出す (印刷 1 枚に科 1 行 + 医師全員が収まる想定)。
  doctorTopN:   DoctorTopN;
  // [2026-08-25 ユーザ指示] 入院経路タブ: サイドバーから「経由」(病院/総CL/その他) で絞り込み。
  //   その他 = 腎CL + 地域ケア (RoutePanel の KEIYU_TO_GROUP と同じ集約)。「合計」= 絞り込みなし。
  routeKeiyu:   RouteKeiyu;
  tab:          TabKey;
  setGranularity: (v: Granularity) => void;
  setIryo:        (v: Iryo) => void;
  setYotei:       (v: Yotei) => void;
  setMetric:      (v: string) => void;
  setCorrFrom:    (v: string) => void;
  setCorrTo:      (v: string) => void;
  setCorrSel:     (v: string) => void;
  setAsOfMonth:   (v: string) => void;
  setPeriodFrom:  (v: string) => void;
  setPeriodTo:    (v: string) => void;
  setPeriodPreset:(v: PeriodPreset) => void;
  setPlFormat:    (v: PlFormat) => void;
  setPlProfit:    (v: PlProfit) => void;
  setRevenueBasis:   (v: RevenueBasis) => void;
  setKpiVolumeBase:  (v: KpiVolumeBase) => void;
  setKbAct:          (v: string) => void;
  setKbActMetric:    (v: "入院収入" | "入院料" | "手術" | "その他") => void;
  setOpeFrom:        (v: string | null) => void;
  setOpeTo:          (v: string | null) => void;
  setOpeRevenueMode: (v: OpeRevenueMode) => void;
  setOpeProcedureQuery: (v: string) => void;
  setMeisaiIo:       (v: "入院" | "外来" | "全") => void;
  setMeisaiBasis:    (v: "ten" | "yen") => void;
  setMeisaiSinryoNo: (v: string) => void;
  setMeisaiDept:     (v: string) => void;
  setFocusedDept: (v: string | null) => void;
  setSurgeryFocusedDept: (v: string | null) => void;
  setSurgeryDim:  (v: "大分類" | "小分類") => void;
  setDpc6:        (v: string | null) => void;
  setAdmdrcd:     (v: string | null) => void;
  setMinCases:    (v: number) => void;
  setDpcfy:       (v: number | null) => void;
  setDateFromOverride: (v: string | null) => void;
  setDateToOverride:   (v: string | null) => void;
  setDiseaseYotei: (v: "合計" | "予定" | "緊急") => void;
  setDiseaseIryo:  (v: "合計" | "急性期") => void;
  setDpcAgg:       (v: "累計" | "月平均") => void;
  setDpcLevel: (v: number) => void;
  setSelectedDpc7:  (v: string | null) => void;
  setSelectedDpc8:  (v: string | null) => void;
  setSelectedDpc10: (v: string | null) => void;
  setSelectedDpc11: (v: string | null) => void;
  setSelectedDpc12: (v: string | null) => void;
  setSelectedDpc13: (v: string | null) => void;
  setSelectedDpc14: (v: string | null) => void;
  setTab:         (v: TabKey) => void;
  setProfitBasis: (v: "在院" | "退院") => void;
  setProfitFrom: (v: string | null) => void;
  setProfitTo:   (v: string | null) => void;
  setProfitIryo: (v: "合計" | "急性期" | "回復期") => void;
  setGun1: (v: number) => void;
  setGun2: (v: number) => void;
  setGun3: (v: number) => void;
  setFocusedDoctor: (v: string | null) => void;
  setBillingReconMode:   (v: BillingReconMode) => void;
  setBillingReconDept:   (v: string | null) => void;
  setBillingReconBunrui: (v: string | null) => void;
  clearBillingReconFilter: () => void;
  setBillingCompareMonth: (v: string | null) => void;
  setDoctorTopN: (v: DoctorTopN) => void;
  setRouteKeiyu: (v: RouteKeiyu) => void;
}

// URL search params から初期値を読む
function readUrl<T extends string>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  const v = new URLSearchParams(window.location.search).get(key);
  return (v as T) || fallback;
}

/**
 * URL に指定キーが **すべて** 明示されているか。
 *
 * [事故対策 2026-07-22] 「既定値を effect で上書きする」処理は、共有URL・静的PDF生成・
 * 別システムからの deep link が **明示的に指定した値まで潰す**。
 * 実際に踏んだ事故: 入院実績タブの既定期間を「最新月単月」に変えたところ、
 * `?from`/`?to` で期間を固定している sjkdev IE11 静的版の「年度月平均」ページが
 * 単月に化けた (bin/generate.mjs は ?tab&g&as&from&to&kvol を明示して開く)。
 *
 * ★既定値の上書きは「URL に明示指定が無いときだけ」に限定すること。
 *   from/to は URL sync の出力対象外だが、外部が付けてくる場合があるので
 *   「sync に出していない = 誰も指定しない」ではない。
 *   この規約は scripts/check-url-override-guard.mjs が CI で機械検査する。
 */
export function urlHasAll(...keys: string[]): boolean {
  if (typeof window === "undefined") return false;
  const p = new URLSearchParams(window.location.search);
  return keys.length > 0 && keys.every((k) => !!p.get(k));
}

// [初期表示 2026-07-22] 最新確定月 (meta.dateMax) をモジュールに保持する。
//   - store 初期化時点では meta を fetch していないため dateMax を知り得ない。
//   - App が meta ロード後に setMetaDateMax() で登録し、asOfMonth の既定解決と
//     URL sync の「既定(=最新月)なら as= を出さない」判定に使う。
//   - これにより asOfMonth のハードコード既定 ("2026-04-01") を撤去し、
//     データが進んでも常に最新確定月が既定になる (古い as= がブックマークに焼き付かない)。
let metaDateMax: string | null = null;
export function setMetaDateMax(v: string): void {
  metaDateMax = v;
}

// [絞り込み保持 2026-07-17・Codex指摘対応] URL 復元時の DPC path 正規化。
// dpc6→10→11→12→13→14 の順で「直近の非 null 上位コードの前方一致」でなければ、
// そこから下位を全部捨てる。アプリ内操作では不整合は作れない (行クリック=フルチェーン、
// 各 setter=下位連鎖クリア) が、手動編集 URL で dpc6 と d10 等が矛盾すると
// 主表 (dpc6 AND selDpc → 0件) と粗利 DPC別 (最深 1 本に縮約) で結果が分岐するため、入口で塞ぐ。
// null (未指定) は素通し (検証 anchor も更新しない)。
function normalizeDpcPath(codes: (string | null)[]): (string | null)[] {
  let prev: string | null = null;
  let broken = false;
  return codes.map((c) => {
    if (broken || c == null) return broken ? null : c;
    if (prev != null && !c.startsWith(prev)) { broken = true; return null; }
    prev = c;
    return c;
  });
}

// タブ名 migration:
//   - 差異分析 / 前年度比較 / 入院実績サマリ → 入院実績 (旧名フォールバック)
//   - DL → 元データ (rename)
//   - 試作 / 予算・前年比較 → 入院実績 (2026-05-26 旧 入院実績 を非表示にし、 予算・前年比較 を 入院実績 に昇格)
// [θ版との相互互換 2026-08-22] θ版クライアント (dist-theta) は build 時に
//   src/shared の「粗利」を機械置換して「GP」にする (scripts/build-theta-client.mjs)。
//   そのため タブキー が 正式版 "粗利分析(医師別なし)" ⇔ θ版 "GP分析(医師別なし)" とズレ、
//   一方で作った URL / 保存プリセットを他方で開くと **タブ未選択の白紙**になっていた。
//   ★ここでは 旧綴りを `\u7C97\u5229`(= 粗利) のエスケープで書く。機械置換は
//     ソース上の生の「粗利」だけを置換するので、この定数は θ 版でも "粗利" のまま残る。
//     結果として 正式版・θ版のどちらのビルドでも 両方の綴りを受け付ける。
const LEGACY_PROFIT = "\u7C97\u5229";                       // "粗利"
const LEGACY_PROFIT_TAB = LEGACY_PROFIT + "\u5206\u6790";   // "粗利分析"

function migrateTab(v: string): TabKey {
  if (v === "差異分析" || v === "前年度比較" || v === "入院実績サマリ") return "入院実績";
  if (v === "DL") return "元データ";
  if (v === "試作" || v === "予算・前年比較") return "入院実績";
  if (v === "疾患深掘り") return "DPC分析";  // 2026-05-26 リネーム
  if (v === "DPC点数") return "DPC分析";     // 2026-05-28 統合: DPC点数 を DPC分析 最下部 panel に統合
  if (v === "行為別") return "行為別売上";   // 2026-07-18 正式昇格: 旧 行為別 タブを非表示・後継の行為別売上へ
  // [θ版との相互互換 2026-08-22] 粗利分析 ⇔ GP分析 をどちらの綴りでも受ける
  if (v === LEGACY_PROFIT_TAB + "(医師別なし)" || v === "GP分析(医師別なし)") return "粗利分析(医師別なし)";
  if (v === LEGACY_PROFIT_TAB || v === "GP分析") return "粗利分析";
  return v as TabKey;
}

/** [θ版との相互互換 2026-08-22] 収入の基準 (URL: krev / 旧 kab / 旧 mm) を
 *  "粗利" と "GP" のどちらの綴りでも受ける。 */
function isProfitBasisValue(v: string): boolean {
  return v === LEGACY_PROFIT || v === "GP";
}

// 表示形式 (旧称: 期間) preset migration:
//   - 単月 / 直近12ヶ月 / 直近12M / 最新月 / 全期間 / カスタム → 月次推移 (2026-05-26 統合)
function migratePreset(v: string): PeriodPreset {
  if (v === "単月" || v === "直近12ヶ月" || v === "直近12M"
      || v === "最新月" || v === "全期間" || v === "カスタム") return "月次推移";
  return v as PeriodPreset;
}

// URL 復元値の DPC 階層 path (prefix 整合を正規化済)
const initDpcPath = normalizeDpcPath([
  readUrl("dpc6", "") || null,
  readUrl("d7", "") || null,
  readUrl("d8", "") || null,
  readUrl("d10", "") || null,
  readUrl("d11", "") || null,
  readUrl("d12", "") || null,
  readUrl("d13", "") || null,
  readUrl("d14", "") || null,
]);

// [初期表示 2026-07-22] タブ切替時に「そのタブ固有の表示形式系 state」を既定へ戻すレジストリ。
//   狙い: タブを開いたら常に適切な初期ビュー (例: PL は必ず「月別」から)。前回いじった
//   キャッシュ的な state (URL/history 復元含む) が次にそのタブを開いたとき残らないようにする。
//   ここに載せるのは各タブ固有の「表示形式 / 指標モード / 集計基準 / タブ専用期間」だけ。
//   --- 保持する (ここに載せない) もの ---
//     * 共有フィルタ: granularity / iryo / yotei / asOfMonth / focusedDept (タブ横断の観測条件)
//     * DPC 絞り込みチェーン: dpc6 / selectedDpc10-14 / dpcfy / dpcLevel
//       (粗利分析→DPC分析 の深掘り持ち回りが意図された機能なのでリセットしない)
//   初期ロード時 (URL 復元) は setTab を経由しないため deep link は壊れない
//   (?tab=経営PL&plf=年度累計 は初回はそのまま。別タブへ行って戻ると既定へ戻る = 意図どおり)。
const TAB_DEFAULTS: Partial<Record<TabKey, Partial<FilterState>>> = {
  // [共通化 2026-07-22] revenueBasis (収入の基準) はタブ横断で引き継ぐため、
  //   TAB_DEFAULTS に載せない (載せるとタブを移った瞬間に既定へ戻ってしまう)。
  "行為別売上":            { kbActMetric: "入院収入" },
  "手術売上":              { opeFrom: null, opeTo: null, opeRevenueMode: "手術売上", opeProcedureQuery: "" },
  "手術":                  { surgeryDim: "大分類", surgeryFocusedDept: null },
  // DPC分析: 指標切替は revenueBasis に統合 (タブ横断で保持)。チェーン(dpc6/d10-14/dpcfy/dpcLevel)も保持。
  "粗利分析(医師別なし)":  { profitBasis: "在院", profitFrom: null, profitTo: null, profitIryo: "急性期", focusedDoctor: null },
  "粗利分析":              { profitBasis: "在院", profitFrom: null, profitTo: null, profitIryo: "急性期", focusedDoctor: null },
  "相関分析":              { corrFrom: "", corrTo: "", corrSel: "" },
  "経営PL":                { plFormat: "月別", plProfit: "経常利益" },
  // [2026-08-03] 医師別マトリックスの行数はタブ固有の表示形式 → 入るたび既定 (25) へ。
  //   deep link (?dtop=30) は setTab を通らないので壊れない。
  "入院実績(医師別)":      { doctorTopN: 25 },
  "請求会計速報":          { billingReconMode: "入院", billingReconDept: null, billingReconBunrui: null },
  // 経由フォーカスはドリルダウン用の一時絞り込みなので、タブに入り直すたびクリア (billingReconDept と同様)。
  "入院経路":              { routeKeiyu: "合計" },
};

export const useFilters = create<FilterState>((set) => ({
  granularity: readUrl<Granularity>("g", "小"),
  iryo:        readUrl<Iryo>("iryo", "合計"),
  dischargeDept: readUrl("ddep", ""),
  setDischargeDept: (v) => set({ dischargeDept: v }),
  yotei:       readUrl<Yotei>("yotei", "合計"),
  metric:      readUrl("metric", "入院収入"),
  // [初期表示 2026-07-22] 実効的な既定 = 最新確定月 (meta.dateMax)。store 初期化時は meta 未取得
  //   なので、in-range な暫定値で初期化し、App / FilterBar が meta ロード後 (1 frame 以内) に
  //   dateMax へ収束させる (?as= 明示時はそれを尊重)。ここは「表示される既定値」ではなく
  //   解決までの一瞬のプレースホルダ (data 範囲内なので空月 fetch = 500 を出さない)。
  //   ★以前の実質バグは URL sync 側で asOfMonth をこの値とハードコード比較していた点で、
  //     そちらは metaDateMax 比較へ是正済 (= 最新月と等しければ as= を出さない)。
  asOfMonth:   readUrl("as", "2026-04-01"),
  periodFrom:  readUrl("from", "2025-04-01"),
  periodTo:    readUrl("to", "2026-04-01"),
  periodPreset:migratePreset(readUrl<PeriodPreset>("preset", "月次推移")),
  // [初期表示 2026-07-22] PL 既定を「年度累計」→「月別」へ (ユーザ指示: 年度累計が初期は不適切)。
  plFormat:    ((): PlFormat => {
    const v = readUrl<string>("plf", "月別");
    return v === "年度累計" || v === "直近12ヶ月累計" ? v : "月別";
  })(),
  // [2026-09-11] 利益の段階。 既定 = 経常利益 (従来の PL タブと同じ見え方)。
  plProfit:    ((): PlProfit => {
    const v = readUrl<string>("plp", "経常利益");
    return v === "医業利益" ? v : "経常利益";
  })(),
  // [共通化 2026-07-22] 収入の基準は krev に一本化。旧 URL (kab=行為別売上 / mm=DPC分析) も
  //   読んで migration する (どれかが「粗利」なら粗利で開く。既定=入院収入)。
  revenueBasis: ((): RevenueBasis => {
    // [θ版との相互互換 2026-08-22] 値の綴りは 正式版="粗利" / θ版="GP"。両方受ける。
    const krev = readUrl<string>("krev", "");
    if (krev) return isProfitBasisValue(krev) ? "粗利" : "入院収入";
    if (isProfitBasisValue(readUrl<string>("kab", ""))) return "粗利";   // 旧 行為別売上 金額基準
    if (isProfitBasisValue(readUrl<string>("mm", "")))  return "粗利";   // 旧 DPC分析 指標切替
    return "入院収入";
  })(),
  kpiVolumeBase:  (readUrl<string>("kvol", "入院") === "在院" ? "在院" : "入院") as KpiVolumeBase,
  kbAct:          readUrl("kba", "手術小計"),
  kbActMetric:    ((): "入院収入" | "入院料" | "手術" | "その他" => {
    const v0 = readUrl<string>("kam", "入院収入");
    const v = v0 === "手術・麻酔" ? "手術"                                    // 2026-07-18 リネーム migrate
            : (v0 === "入院基本料" || v0 === "特定入院料") ? "入院料"          // 2026-08-17 統合 migrate
            : v0;
    return (["入院料", "手術", "その他"] as const).includes(v as never) ? v as never : "入院収入";
  })(),
  opeFrom:        readUrl<string>("osf", "") || null,
  opeTo:          readUrl<string>("ost", "") || null,
  opeRevenueMode: ((): OpeRevenueMode => {
    const v = readUrl<string>("orv", "");
    if (v === "gi") return "手技売上";
    if (v === "zy") return "材料薬剤費";
    return "手術売上";
  })(),
  opeProcedureQuery: readUrl<string>("opq", ""),
  meisaiIo:       ((): "入院" | "外来" | "全" => { const v = readUrl<string>("mio", "入院"); return v === "外来" || v === "全" ? v : "入院"; })(),
  meisaiBasis:    (readUrl<string>("mbasis", "ten") === "yen" ? "yen" : "ten") as "ten" | "yen",
  meisaiSinryoNo: readUrl("msin", ""),
  meisaiDept:     readUrl("mdept", ""),
  focusedDept: readUrl("dept", "") || null,
  surgeryFocusedDept: readUrl("sdept", "") || null,
  surgeryDim: ((readUrl("sdim", "大分類") as string) === "小分類" ? "小分類" : "大分類"),
  dpc6:        initDpcPath[0],
  admdrcd:     readUrl("dr", "") || null,
  minCases:    (() => {
    // 2026-05-28: 「新規受持件数 N 以上」 → 「入院患者延数 N 以上」 に機能変更。
    // 列名は store 互換のため minCases のまま。 サーバ側 HAVING は SUM(inpatient) >= ? に変更済。
    // default = 0 (= ユーザ指示、 filter 無効が初期。 N を上げて絞り込みは任意)。
    const v = parseInt(readUrl("n", "") || "", 10);
    return Number.isFinite(v) && v >= 0 ? v : 0;
  })(),
  dpcfy: readDpcYear(readUrl<string>("fy", "")),
  diseaseAutoPeriod: null,
  ...diseaseUrlPeriod(readUrl("dfrom", "") || null, readUrl("dto", "") || null,
    readDpcYear(readUrl<string>("fy", ""))),
  corrFrom: readUrl("cfrom", ""),        // "" = 直近1年 auto (panel/FilterBar が dateMax-11 で解決)
  corrTo:   readUrl("cto", ""),          // "" = 最新月 auto (panel が meta.dateMax で解決)
  corrSel:  readUrl("csel", ""),         // "" = 既定セット (panel が解決)
  diseaseYotei:    ((): "合計" | "予定" | "緊急" => {
    const v = readUrl<string>("dy", "合計");
    return v === "予定" || v === "緊急" ? v : "合計";
  })(),
  diseaseIryo:     ((): "合計" | "急性期" => {
    // 2026-05-26: 回復期 は UI から削除 (DPC 分析 では切り分け意味薄い)。
    // 旧 URL `?diryo=回復期` 互換: 「合計」 にマイグレーション。
    const v = readUrl<string>("diryo", "合計");
    return v === "急性期" ? v : "合計";
  })(),
  dpcAgg: readUrl<string>("dagg", "") === "月平均" ? "月平均" : "累計",
  dpcLevel: ((): number => {
    const v = Number(readUrl<string>("dlv", "6"));
    return (DPC_LEVELS as readonly number[]).includes(v) ? v : 6;
  })(),
  selectedDpc7:  initDpcPath[1],
  selectedDpc8:  initDpcPath[2],
  selectedDpc10: initDpcPath[3],
  selectedDpc11: initDpcPath[4],
  selectedDpc12: initDpcPath[5],
  selectedDpc13: initDpcPath[6],
  selectedDpc14: initDpcPath[7],
  // ★封鎖中は URL の ?pbasis=退院 も無視して "在院" に落とす (既存リンク・ブックマーク対策)。
  profitBasis: (PROFIT_BASIS_DISCHARGE_ENABLED && readUrl<string>("pbasis", "在院") === "退院" ? "退院" : "在院"),
  profitFrom: readUrl("pfrom", "") || null,
  profitTo:   readUrl("pto", "") || null,
  profitIryo: ((): "合計" | "急性期" | "回復期" => {
    const v = readUrl<string>("piryo", "急性期");
    return v === "合計" || v === "回復期" ? v : "急性期";
  })(),
  // [2026-07-18 ユーザ指示] 群分類 default = 1群≥115,000 / 2群≥85,000 / 3群≥65,000
  // [正式版GP 2026-08-25] 85,000 / 75,000 / 65,000 (1万円刻み)。
  //   ★当初は「正式版と同じ順位位置」に寄せた 95,000/75,000/60,000 を入れていたが、
  //     ユーザ裁定でキリのよい 1万円刻みへ変更。既定値の正本は GUN_DEFAULT。
  gun1: Number(readUrl("g1", String(GUN_DEFAULT[0]))) || GUN_DEFAULT[0],
  gun2: Number(readUrl("g2", String(GUN_DEFAULT[1]))) || GUN_DEFAULT[1],
  gun3: Number(readUrl("g3", String(GUN_DEFAULT[2]))) || GUN_DEFAULT[2],
  focusedDoctor: readUrl("fdr", "") || null,
  billingReconMode: ((): BillingReconMode => {
    const v = readUrl<string>("brm", "入院");
    if (v === "病院全体" || v === "入外合計") return "入外合計";  // 旧 URL 値 病院全体 → 入外合計 に migration
    if (v === "外来") return "外来";
    return "入院";
  })(),
  billingReconDept:   readUrl("brd", "") || null,
  billingReconBunrui: readUrl("brb", "") || null,
  billingCompareMonth: readUrl("bcm", "") || null,
  // [2026-08-03] 医師別マトリックスの行数 (20/25/30・既定 25 = A4 横 1 枚に収まる行数)
  doctorTopN: ((): DoctorTopN => {
    const v = Number(readUrl<string>("dtop", "25"));
    return (DOCTOR_TOP_N_OPTIONS as readonly number[]).includes(v) ? (v as DoctorTopN) : 25;
  })(),
  routeKeiyu:  ((): RouteKeiyu => {
    const v = readUrl<string>("rk", "合計");
    return v === "病院" || v === "総CL" || v === "その他" ? v : "合計";
  })(),
  tab:         migrateTab(readUrl<TabKey>("tab", "入院実績(前年度・予算比較)")),
  setGranularity: (v) => set({
    granularity: v,
    // 粒度切替時は focus を解除 (科リストが変わる) +
    // DPC6 / 医師 / drill-down path をクリア (空表 or 不整合 fetch を防ぐ)
    focusedDept: null,
    dpc6: null,
    admdrcd: null,
    selectedDpc7:  null,
    selectedDpc8:  null,
    selectedDpc10: null,
    selectedDpc11: null,
    selectedDpc12: null,
    selectedDpc13: null,
    selectedDpc14: null,
  }),
  setIryo:        (v) => set({ iryo: v }),
  setYotei:       (v) => set({ yotei: v }),
  setMetric:      (v) => set({ metric: v }),
  setCorrFrom:    (v) => set({ corrFrom: v }),
  setCorrTo:      (v) => set({ corrTo: v }),
  setCorrSel:     (v) => set({ corrSel: v }),
  setAsOfMonth:   (v) => set({ asOfMonth: v, periodTo: v }),
  setPeriodFrom:  (v) => set({ periodFrom: v }),
  setPeriodTo:    (v) => set({ periodTo: v }),
  setPeriodPreset:(v) => set({ periodPreset: v }),
  setPlFormat:    (v) => set({ plFormat: v }),
  setPlProfit:    (v) => set({ plProfit: v }),
  setRevenueBasis:   (v) => set({ revenueBasis: v }),
  setKpiVolumeBase:  (v) => set({ kpiVolumeBase: v }),
  setKbAct:          (v) => set({ kbAct: v }),
  setKbActMetric:    (v) => set({ kbActMetric: v }),
  setOpeFrom:        (v) => set({ opeFrom: v }),
  setOpeTo:          (v) => set({ opeTo: v }),
  setOpeRevenueMode: (v) => set({ opeRevenueMode: v }),
  setOpeProcedureQuery: (v) => set({ opeProcedureQuery: v }),
  setMeisaiIo:       (v) => set({ meisaiIo: v }),
  setMeisaiBasis:    (v) => set({ meisaiBasis: v }),
  setMeisaiSinryoNo: (v) => set({ meisaiSinryoNo: v }),
  setMeisaiDept:     (v) => set({ meisaiDept: v }),
  setProfitBasis: (v) => set({ profitBasis: PROFIT_BASIS_DISCHARGE_ENABLED ? v : "在院", focusedDoctor: null }),
  setProfitFrom: (v) => set({ profitFrom: v }),
  setProfitTo:   (v) => set({ profitTo: v }),
  setProfitIryo: (v) => set({ profitIryo: v }),
  setGun1: (v) => set({ gun1: v }),
  setGun2: (v) => set({ gun2: v }),
  setGun3: (v) => set({ gun3: v }),
  setFocusedDoctor: (v) => set({ focusedDoctor: v }),
  // モード切替時はドリル選択(表示科/診療No)をクリア(モード間で意味が変わるため)。
  setBillingReconMode: (v) => set({ billingReconMode: v, billingReconDept: null, billingReconBunrui: null }),
  // 表示科を変えたら下位の診療No選択はリセット(古い科の診療Noで③が空になるのを防ぐ)。
  setBillingReconDept: (v) => set({ billingReconDept: v, billingReconBunrui: null }),
  setBillingReconBunrui: (v) => set({ billingReconBunrui: v }),
  clearBillingReconFilter: () => set({ billingReconDept: null, billingReconBunrui: null }),
  setBillingCompareMonth: (v) => set({ billingCompareMonth: v }),
  setDoctorTopN: (v) => set({ doctorTopN: v }),
  setRouteKeiyu: (v) => set({ routeKeiyu: v }),
  setFocusedDept: (v) => set({
    focusedDept: v,
    // 診療科変更時は DPC6 / 医師 / drill-down path をクリア
    // (空表 or 不整合 fetch を防ぐ)
    dpc6: null,
    admdrcd: null,
    selectedDpc7:  null,
    selectedDpc8:  null,
    selectedDpc10: null,
    selectedDpc11: null,
    selectedDpc12: null,
    selectedDpc13: null,
    selectedDpc14: null,
  }),
  setSurgeryFocusedDept: (v) => set({ surgeryFocusedDept: v }),
  // 粒度切替時は dept 名が変わる (大↔小で 外科 ⇄ 消化器/乳腺内分泌/呼吸器) ので focus を解除
  setSurgeryDim:  (v) => set({ surgeryDim: v, surgeryFocusedDept: null }),
  setDpc6:        (v) => set({
    // 疾患 (DPC6) を変更 / クリアしたら drill-down path を全リセット
    dpc6: v,
    selectedDpc7:  null,
    selectedDpc8:  null,
    selectedDpc10: null,
    selectedDpc11: null,
    selectedDpc12: null,
    selectedDpc13: null,
    selectedDpc14: null,
  }),
  setAdmdrcd:     (v) => set({ admdrcd: v }),
  setMinCases:    (v) => set({ minCases: Math.max(0, Math.floor(v)) }),
  setDpcfy:       (v) => set({
    dpcfy: v,
    diseaseAutoPeriod: null,
    // DPC 年度変更時は path リセット (年度間で DPC6/drill-down 値の連続性が無いため)
    dpc6: null,
    selectedDpc7:  null,
    selectedDpc8:  null,
    selectedDpc10: null,
    selectedDpc11: null,
    selectedDpc12: null,
    selectedDpc13: null,
    selectedDpc14: null,
    // 期間 override もクリア → DiseasePanel 側の effect が「年度 range 内のデータある直近 12 ヶ月」
    // を disease_period_range で自動セットする (2026-07-17 に select 内 fetch から一元化)
    dateFromOverride: null,
    dateToOverride: null,
  }),
  setDateFromOverride: (v) => set({
    dateFromOverride: v,
    // 期間変更時も DPC6 / drill-down path リセット
    // (期間外の dpc6 で空表になるのを防ぐ。 admdrcd は維持 = 期間絞り込みで医師 filter
    //  を毎回外したくないケースを想定)
    dpc6: null,
    selectedDpc7:  null,
    selectedDpc8:  null,
    selectedDpc10: null,
    selectedDpc11: null,
    selectedDpc12: null,
    selectedDpc13: null,
    selectedDpc14: null,
  }),
  setDateToOverride:   (v) => set({
    dateToOverride: v,
    dpc6: null,
    selectedDpc7:  null,
    selectedDpc8:  null,
    selectedDpc10: null,
    selectedDpc11: null,
    selectedDpc12: null,
    selectedDpc13: null,
    selectedDpc14: null,
  }),
  setDiseaseYotei: (v) => set({ diseaseYotei: v }),
  setDiseaseIryo:  (v) => set({ diseaseIryo: v }),
  setDpcAgg:       (v) => set({ dpcAgg: v }),
  // [絞り込み保持 2026-07-17] 階層切替 (疾患別⇄手術別⇄処置1別⇄処置2別⇄完全) では
  // dpc6/selectedDpc10-14 をリセットしない。適用範囲は fetch 側で「表示階層以下の絞り込みは
  // 使わない」ようゲートする (下位階層の絞り込みを上位階層に反映させない)。
  setDpcLevel: (v) => set({ dpcLevel: v }),
  // selectedDpcXX: 上位を変更すると下位を全部リセット (= drill-down のとき下表が
  // 古い親 path で fetch してしまうのを防ぐ)
  setSelectedDpc7: (v) => set({
    selectedDpc7: v,
    selectedDpc8: null,
    selectedDpc10: null,
    selectedDpc11: null,
    selectedDpc12: null,
    selectedDpc13: null,
    selectedDpc14: null,
  }),
  setSelectedDpc8: (v) => set({
    selectedDpc8: v,
    selectedDpc10: null,
    selectedDpc11: null,
    selectedDpc12: null,
    selectedDpc13: null,
    selectedDpc14: null,
  }),
  setSelectedDpc10: (v) => set({
    selectedDpc10: v,
    selectedDpc11: null,
    selectedDpc12: null,
    selectedDpc13: null,
    selectedDpc14: null,
  }),
  setSelectedDpc11: (v) => set({
    selectedDpc11: v,
    selectedDpc12: null,
    selectedDpc13: null,
    selectedDpc14: null,
  }),
  setSelectedDpc12: (v) => set({
    selectedDpc12: v,
    selectedDpc13: null,
    selectedDpc14: null,
  }),
  setSelectedDpc13: (v) => set({
    selectedDpc13: v,
    selectedDpc14: null,
  }),
  setSelectedDpc14: (v) => set({ selectedDpc14: v }),
  // [初期表示 2026-07-22] タブを実際に切り替えたとき、そのタブ固有の表示形式系 state を
  //   既定へリセット (TAB_DEFAULTS)。共有フィルタ・DPC チェーンは温存 (レジストリに載せない)。
  //   同一タブの再クリックはリセットしない (無操作)。
  setTab:         (v) => set((s) => (v === s.tab ? { tab: v } : { tab: v, ...(TAB_DEFAULTS[v] ?? {}) })),
}));

// URL state sync: filter 変化のたびに ?g=小&iryo=合計&... に反映 (history.replaceState)
// [戻る/進む 2026-09-28 ユーザ指示「ホームページ全体で進むと戻るを有効に」(DPC分析だけ→全タブへ拡張)]
//   従来は URL を replaceState で書くだけで履歴を積まず、ブラウザ/マウスの「戻る」でページごと離れていた。
//   ★規則 = 「ユーザ操作 (pointerdown/keydown/change/input) 1 回につき履歴は最大 1 つ」。
//     - 操作のあと最初に条件 (store の全項目) が変わった書き込みは pushState、それ以降の書き込み
//       (操作から遅れて届く自動設定・データ到着後の既定値の補完など) は replaceState で同じ履歴に畳む。
//     - 操作なしの書き込み (初期表示の自動設定) も replace。これが無いと「戻る → 自動設定で 1 段積まれる
//       → また戻る…」のループになる。
//   履歴の state に store のスナップショットを持たせ、popstate で丸ごと復元する (URL の再解釈はしない)。
//   ★マウスの戻るボタン (button 3/4) も pointerdown を出すので、操作には数えない。
//   ★各パネルの useState (表の並べ替え等) は store に無いので復元対象外。
type FilterSnap = Record<string, unknown>;
const filterSnap = (s: FilterState): FilterSnap =>
  Object.fromEntries(Object.entries(s).filter(([, v]) => typeof v !== "function"));
const HISTORY_GESTURE_WINDOW_MS = 3000;

if (typeof window !== "undefined") {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let inputSeq = 0, inputAt = 0, pushedSeq = 0;
  const onInput = (e: Event) => {
    if (e instanceof PointerEvent && e.button > 2) return;   // マウスの戻る/進むボタン
    inputSeq += 1; inputAt = Date.now();
  };
  window.addEventListener("pointerdown", onInput, true);
  window.addEventListener("keydown", onInput, true);
  // select の選択肢を選ぶ瞬間は pointerdown/keydown が出ない (開いてから迷うと 3 秒を過ぎる)。
  // change/input はユーザ操作でしか発火しない (プログラムからの値代入では出ない) ので操作に数える。
  window.addEventListener("change", onInput, true);
  window.addEventListener("input", onInput, true);
  // いま表示している履歴エントリのスナップショット。初回ロードのエントリにも持たせる (戻り先になる)。
  let lastSnapJson = JSON.stringify(filterSnap(useFilters.getState()));
  window.history.replaceState({ ...(window.history.state ?? {}), filters: JSON.parse(lastSnapJson) }, "", window.location.href);
  window.addEventListener("popstate", (e) => {
    const snap = (e.state as { filters?: FilterSnap } | null)?.filters;
    if (!snap) return;
    pushedSeq = inputSeq;   // 復元は新しい操作ではない (この後の書き込みは replace)
    lastSnapJson = JSON.stringify(snap);
    useFilters.setState(snap as Partial<FilterState>);
  });
  useFilters.subscribe((s) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      const params = new URLSearchParams();
      // 分析レポートの独立した条件と、詳細タブへの明示期間を再読込でも保持する。
      const currentParams = new URLSearchParams(window.location.search);
      for(const key of ["obase","ocomp","osite","odept","oframe","oview","lmode","lorigin","osearch","lstart","lend"]){const value=currentParams.get(key);if(value!==null)params.set(key,value);}
      if (s.tab === "AI分析") for (const key of ["reportMonth", "reportDept", "reportMode"]) {
        const value = currentParams.get(key); if (value) params.set(key, value);
      }
      if (s.tab === "紹介・入院分析") {
        params.set("from", s.periodFrom); params.set("to", s.periodTo);
        for (const key of ["rtype","rsite","rbasis","rdept","rdepts","rmetric","rview","rfac"]) {const v=currentParams.get(key); if(v)params.set(key,v);}
      }
      if (currentParams.has("ai")) {
        params.set("ai", "1"); params.set("from", s.periodFrom); params.set("to", s.periodTo);
        params.set("pc", "1");
      }
      if (s.granularity !== "小")          params.set("g", s.granularity);
      if (s.iryo !== "合計")               params.set("iryo", s.iryo);
      if (s.tab === "退院先分岐") { params.set("from",s.periodFrom); params.set("to",s.periodTo); if(s.dischargeDept) params.set("ddep",s.dischargeDept); }
      if (s.yotei !== "合計")              params.set("yotei", s.yotei);
      if (s.metric !== "入院収入")         params.set("metric", s.metric);
      // asOfMonth: 既定 = 最新確定月 (metaDateMax) のときは URL に出さない (= クリーン URL・
      //   ブックマークに古い月を焼き付けない)。metaDateMax 未登録 (meta ロード前) は出さない。
      //   ユーザが別の月を選んだとき (asOfMonth ≠ 最新月) のみ as= を付ける (deep link 用)。
      if (metaDateMax && s.asOfMonth && s.asOfMonth !== metaDateMax) params.set("as", s.asOfMonth);
      if (s.periodPreset !== "月次推移")      params.set("preset", s.periodPreset);
      if (s.plFormat !== "月別")             params.set("plf", s.plFormat);
      if (s.plProfit !== "経常利益")         params.set("plp", s.plProfit);
      if (s.revenueBasis !== "入院収入")    params.set("krev", s.revenueBasis);
      if (s.kpiVolumeBase !== "入院")       params.set("kvol", s.kpiVolumeBase);
      if (s.profitBasis !== "在院")         params.set("pbasis", s.profitBasis);
      if (s.profitFrom)                    params.set("pfrom", s.profitFrom);
      if (s.profitTo)                      params.set("pto", s.profitTo);
      if (s.profitIryo !== "急性期")        params.set("piryo", s.profitIryo);
      if (s.gun1 !== GUN_DEFAULT[0])       params.set("g1", String(s.gun1));
      if (s.gun2 !== GUN_DEFAULT[1])       params.set("g2", String(s.gun2));
      if (s.gun3 !== GUN_DEFAULT[2])       params.set("g3", String(s.gun3));
      if (s.kbAct !== "手術小計")           params.set("kba", s.kbAct);
      if (s.kbActMetric !== "入院収入")     params.set("kam", s.kbActMetric);
      if (s.opeFrom)                        params.set("osf", s.opeFrom);
      if (s.opeTo)                          params.set("ost", s.opeTo);
      if (s.opeRevenueMode === "手技売上")  params.set("orv", "gi");
      if (s.opeRevenueMode === "材料薬剤費") params.set("orv", "zy");
      if (s.opeProcedureQuery)              params.set("opq", s.opeProcedureQuery);
      if (s.meisaiIo !== "入院")            params.set("mio", s.meisaiIo);
      if (s.meisaiBasis !== "ten")          params.set("mbasis", s.meisaiBasis);
      if (s.meisaiSinryoNo)                 params.set("msin", s.meisaiSinryoNo);
      if (s.meisaiDept)                     params.set("mdept", s.meisaiDept);
      if (s.focusedDept)                   params.set("dept", s.focusedDept);
      if (s.surgeryFocusedDept)            params.set("sdept", s.surgeryFocusedDept);
      if (s.surgeryDim !== "大分類")        params.set("sdim", s.surgeryDim);
      if (s.dpc6)                          params.set("dpc6", s.dpc6);
      if (s.admdrcd)                       params.set("dr", s.admdrcd);
      if (s.minCases !== 0)                params.set("n", String(s.minCases));
      // dpcfy: default = 2026 なので、 2026 は URL に出さず、 null は明示的に "all"、
      //   2024 は "2024" として表現する (URL ↔ state の往復性を担保)。
      if (s.dpcfy === null)                params.set("fy", "all");
      else if (s.dpcfy !== 2026)           params.set("fy", String(s.dpcfy));
      if (s.dateFromOverride)              params.set("dfrom", s.dateFromOverride);
      if (s.dateToOverride)                params.set("dto", s.dateToOverride);
      if (s.corrFrom)                      params.set("cfrom", s.corrFrom);
      if (s.corrTo)                        params.set("cto", s.corrTo);
      if (s.corrSel)                       params.set("csel", s.corrSel);
      if (s.diseaseYotei !== "合計")       params.set("dy", s.diseaseYotei);
      if (s.diseaseIryo !== "合計")        params.set("diryo", s.diseaseIryo);
      if (s.dpcLevel !== 6)                params.set("dlv", String(s.dpcLevel));
      if (s.dpcAgg !== "累計")             params.set("dagg", s.dpcAgg);
      if (s.selectedDpc7)                  params.set("d7", s.selectedDpc7);
      if (s.selectedDpc8)                  params.set("d8", s.selectedDpc8);
      if (s.selectedDpc10)                 params.set("d10", s.selectedDpc10);
      if (s.selectedDpc11)                 params.set("d11", s.selectedDpc11);
      if (s.selectedDpc12)                 params.set("d12", s.selectedDpc12);
      if (s.selectedDpc13)                 params.set("d13", s.selectedDpc13);
      if (s.selectedDpc14)                 params.set("d14", s.selectedDpc14);
      if (s.billingReconMode !== "入院")   params.set("brm", s.billingReconMode);
      if (s.billingReconDept)              params.set("brd", s.billingReconDept);
      if (s.billingReconBunrui)            params.set("brb", s.billingReconBunrui);
      if (s.billingCompareMonth)           params.set("bcm", s.billingCompareMonth);
      if (s.doctorTopN !== 25)             params.set("dtop", String(s.doctorTopN));
      if (s.routeKeiyu !== "合計")         params.set("rk", s.routeKeiyu);
      if (s.tab !== "入院実績(前年度・予算比較)") params.set("tab", s.tab);
      const qs = params.toString();
      const url = qs ? `?${qs}` : window.location.pathname;
      const snap = filterSnap(s);
      const snapJson = JSON.stringify(snap);
      const freshGesture = inputSeq > pushedSeq && Date.now() - inputAt < HISTORY_GESTURE_WINDOW_MS;
      if (snapJson !== lastSnapJson && freshGesture) {
        window.history.pushState({ filters: snap }, "", url);
        pushedSeq = inputSeq;
      } else {
        window.history.replaceState({ ...(window.history.state ?? {}), filters: snap }, "", url);
      }
      lastSnapJson = snapJson;
    }, 150);
  });
}

type DpcSelKey = "selectedDpc7" | "selectedDpc8" | "selectedDpc10" | "selectedDpc11"
  | "selectedDpc12" | "selectedDpc13" | "selectedDpc14";
const DPC_SEL_KEY: Record<number, DpcSelKey> = {
  7: "selectedDpc7", 8: "selectedDpc8", 10: "selectedDpc10", 11: "selectedDpc11",
  12: "selectedDpc12", 13: "selectedDpc13", 14: "selectedDpc14",
};
type DpcPath = { dpc6: string | null } & Record<DpcSelKey, string | null>;

/** 表示階層 lvl で選択中のコード (6 桁なら dpc6)。行ハイライト・選択中バー用。 */
export function selectedDpcAt(f: DpcPath, lvl: number): string | null {
  return lvl === 6 ? f.dpc6 : (DPC_SEL_KEY[lvl] ? f[DPC_SEL_KEY[lvl]] : null);
}

/** 階層 lvl 以下 (lvl を含む深い側) の選択を解除する。lvl=6 なら疾患ごと解除。 */
export function clearDpcFrom(lvl: number) {
  const patch: Partial<DpcPath> = lvl <= 6 ? { dpc6: null } : {};
  for (const L of DPC_LEVELS) if (L >= lvl && L > 6) patch[DPC_SEL_KEY[L]] = null;
  useFilters.setState(patch);
}

// [絞り込み保持 2026-07-17] DPC 主表 (DPC分析 / 粗利分析 DPC別) の行クリック共通処理。
//   - クリック → その階層までの絞り込み path (dpc6 + selectedDpc7..14 の前方一致チェーン) を
//     丸ごとセットし (= サイドバーの 疾患 / 手術分類 / 処置1分岐 / 処置2分岐 に「飛んでくる」)、
//     次の階層タブへ自動遷移する。
//   - [次の分岐へ 2026-09-28 ユーザ指示] 遷移先は「次に分岐がある階層」(server の next_level)。
//     例: くも膜下出血 (7桁目は分岐なし・8桁目=JCS) は 疾患別 → 8桁目 → 手術別 と飛ぶ。
//     分岐の無い階層は 1 行しか出ないので、そこに止まらせない。
//     nextLvl 未指定 (粗利分析 DPC別など next_level を持たない表) は従来の固定順 NEXT_DPC_LEVEL。
//     nextLvl === null は「これ以上 drill しない」(14桁行・14桁未確定の行) → タブは動かさない。
//   - 同じ行を再クリック → 「その階層以下」だけ解除 (上位の絞り込みは保持・タブ遷移しない)
//   - 上位階層の選択は前方一致の prefix として埋める (dpcChainPatch)。ただし桁が x の階層は埋めない。
const DPC_SEG_START: Record<number, number> = { 7: 6, 8: 7, 10: 8, 11: 10, 12: 11, 13: 12, 14: 13 };
/** コード code を階層 upto まで選んだときの selectedDpc7..14 (upto より深い側は null)。
 *  ★桁が全部 x の中間階層 (= その疾患では分岐しない) は埋めない。埋めると「x」で前方一致する
 *    絞り込みが残り、14 桁未確定 (コードが 6 桁だけ) の症例が黙って落ちる。後でその階層以下を
 *    解除 (再クリック/パンくず) したとき件数が元に戻らなくなる (2026-09-28 実測で 2.2→2.4 件/月)。
 *    クリックした階層そのものは x でも埋める (ユーザが選んだ行なので)。 */
export function dpcChainPatch(code: string, upto: number): Partial<Record<DpcSelKey, string | null>> {
  const patch: Partial<Record<DpcSelKey, string | null>> = {};
  for (const L of DPC_LEVELS) {
    if (L === 6) continue;
    const seg = code.slice(DPC_SEG_START[L], L);
    const fill = L <= upto && code.length >= L && (L === upto || !/^[xX]+$/.test(seg));
    patch[DPC_SEL_KEY[L]] = fill ? code.slice(0, L) : null;
  }
  return patch;
}
const NEXT_DPC_LEVEL: Record<number, number> = { 6: 10, 7: 8, 8: 10, 10: 11, 11: 12, 12: 14, 13: 14, 14: 14 };
// [連打ガード 2026-09-28 ユーザ報告「疾患別のあとに手術の分岐があるのに処置2に飛ぶ」]
//   行クリックで次の階層の表に切り替わった直後、同じ位置への 2 回目のクリック (ダブルクリック・連打) が
//   **新しい表の行** に当たり、手術を勝手に選んで 処置1/2 まで進んでいた (150ms 間隔の 2 クリックで再現)。
//   drill した直後の一定時間は次の行クリックを捨てる。
const DPC_DRILL_GUARD_MS = 600;
let lastDpcDrillAt = 0;
export function applyDpcRowSelection(code: string, lvl: number, nextLvl?: number | null) {
  if (Date.now() - lastDpcDrillAt < DPC_DRILL_GUARD_MS) return;
  const s = useFilters.getState();
  const dpc6Code = code.slice(0, 6);
  const alreadyHere = s.dpc6 === dpc6Code && selectedDpcAt(s, lvl) === (lvl === 6 ? dpc6Code : code);
  if (alreadyHere) {
    clearDpcFrom(lvl);
    return;
  }
  const patch: Partial<FilterState> = { dpc6: dpc6Code, ...dpcChainPatch(code, lvl) };
  patch.dpcLevel = nextLvl === undefined ? (NEXT_DPC_LEVEL[lvl] ?? lvl) : (nextLvl ?? lvl);
  if (patch.dpcLevel !== lvl) lastDpcDrillAt = Date.now();
  useFilters.setState(patch);
}

// [絞り込み保持 2026-07-17] 「表示階層以下の絞り込みは上位階層に反映させない」ゲート。
//   表示階層 lvl で有効な最深の DPC 絞り込みコード (7 桁以上 = dpc14 前方一致で使う) を返す。
//   dpc6 (6 桁) は既存の dpc6 パラメータ側で常時適用されるため、ここでは 7 桁以上のみ。
//   include14=false なら 13/14 桁 (副傷病・重症度) の選択は使わない。
export function effectiveDpcSelection(f: { dpcLevel: number } & DpcPath, include14 = true): string | null {
  const lvl = f.dpcLevel;
  for (const L of [14, 13, 12, 11, 10, 8, 7]) {
    if (L > lvl || (!include14 && L >= 13)) continue;
    const v = f[DPC_SEL_KEY[L]];
    // 14桁未確定 (コードが 6 桁しか無い) の行を選ぶと prefix が 6 桁になる → dpc6 で足りるので捨てる
    if (v && v.length >= 7) return v;
  }
  return null;
}

// 月計算 helper
export function monthShift(yyyymmdd: string, delta: number): string {
  const [y, m, d] = yyyymmdd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1 + delta, d));
  return [
    dt.getUTCFullYear(),
    String(dt.getUTCMonth() + 1).padStart(2, "0"),
    String(dt.getUTCDate()).padStart(2, "0"),
  ].join("-");
}

// 表示形式 preset の解決 (asOfMonth から periodFrom / periodTo を返す)
export function resolvePeriod(
  preset: PeriodPreset,
  asOfMonth: string,
  _dateMin: string,
  _dateMax: string,
): { periodFrom: string; periodTo: string } {
  switch (preset) {
    case "月次推移":
    case "直近12ヶ月平均":
      // 直近13ヶ月 (= 前年同月 〜 当月)。 マトリックスで前年同月を時系列の先頭に出すため
      // 2026-06-15 に -11(12ヶ月) → -12(13ヶ月) に拡張。 Cards は単月値、 Charts/Table は推移。
      // 「直近12ヶ月平均」も同じ window を fetch し、 集計列/カード値だけ末尾12ヶ月平均に切替 (2026-06-16)。
      return { periodFrom: monthShift(asOfMonth, -12), periodTo: asOfMonth };
    case "年度累計": {
      // 4月始まり: asOfMonth が 4月以降ならその年度の4月、 3月までならその前年4月
      const [y, m] = asOfMonth.split("-").map(Number);
      const fyStart = m >= 4 ? y : y - 1;
      return { periodFrom: `${fyStart}-04-01`, periodTo: asOfMonth };
    }
  }
}
